import {Tooltip} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {Box, Typography} from '@viptech/react-components'
import {useEffect, useState} from 'react'
import CustomCalendar from 'src/components/customCalendar/CustomCalendar'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import {MultiSelect, MultiSelectContent, MultiSelectItem, MultiSelectScrollView, MultiSelectValue, SelectValue} from 'src/components/multi-select/MultiSelect'
import {useCompany} from 'src/contexts/CompanyContext'
import useClients from 'src/hooks/api/client/useClients'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import RefreshIcon from 'src/images/RefreshIcon'
import {Camera} from 'src/services/api'
import {formatDateTime} from 'src/utilities/datetimeUtils'
import {QueryKeyEnum} from 'src/utilities/enums/QueryKeyEnum'
import ExportDataButton from './ExportDataButton'

type Props = {
  dashboardSelected: 'events' | 'detections' | 'analytic' | 'synthetic'
  dashboardsOptions: Array<{id: 'events' | 'detections' | 'analytic' | 'synthetic'; label: string}>
  filter: {cameraIds: Array<number>; clientIds: Array<number>; start: string; end: string; eventStatuses: Array<string>}
  setDashboardSelected: (value: 'events' | 'detections' | 'analytic' | 'synthetic') => void
  initialDates: [Date, Date]
  onChangeFilter: (start: string, end: string, clientIds?: Array<number>, cameraIds?: Array<number>, eventStatuses?: Array<string>) => void
}

const EventStatusNameValues = ['DESPACHADO', 'IGNORADO', 'ERRO', 'INICIADO', 'PROCESSADO', 'EXPIRADO']
function DashboardHeader({filter, initialDates, dashboardSelected, setDashboardSelected, onChangeFilter, dashboardsOptions}: Props) {
  const {selectedCompanyId, setSelectedCompanyId, companies} = useCompany()
  const queryClient = useQueryClient()
  const {styledTheme} = usePersonalizedThemeByCompany()

  const {data: clients} = useClients({selectedCompanies: [selectedCompanyId], includes: ['cameras'], hasPermission: selectedCompanyId !== 0})
  const [dateFilter, setDateFilter] = useState<{start: Date; end: Date}>({start: initialDates[0], end: initialDates[1]})
  const [refreshedAt, setRefreshedAt] = useState<Date>(new Date())
  const [isRotating, setIsRotating] = useState(false)
  const [camerasSelected, setCamerasSelected] = useState<SelectValue[]>([])
  const [eventStatusSelected, setEventStatusSelected] = useState<SelectValue[]>([])
  const [clientsSelected, setClientsSelected] = useState<SelectValue[]>([])

  const [cameraOptions, setCameraOptions] = useState<Camera[]>([])

  const [companySeleted, setCompanySelected] = useState<DropdownItem>()

  useEffect(() => {
    const company = companies?.find((com) => com.id === selectedCompanyId)
    if (company) {
      setSelectedCompanyId(company.id)
      setCompanySelected({id: String(company.id), label: company.name})
    }
  }, [companies])

  const handleRefresh = () => {
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_OBJECTS_COUNT]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_AGE_AND_GENDER]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_FACIAL_RECOGNITION]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_LPR]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_GENDER_SERIES]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_OBJECT_SERIES]})

    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_CAMERAS]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_EVENTS_COUNT_BY_CAMERA]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_EVENTS_BY_STATUS]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_EVENTS_BY_HOUR]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_FACE_SERIES]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_HEATMAP]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_COUNTING_OBJECTS_SERIES]})
    queryClient.invalidateQueries({queryKey: [QueryKeyEnum.DASHBOARD_COUNTING_OBJECTS]})

    setRefreshedAt(new Date())
    setIsRotating((prev) => !prev)
  }

  useEffect(() => {
    const clientIds = clientsSelected.map((client) => Number(client.value))
    const cameraIds = camerasSelected.map((camera) => Number(camera.value))
    const eventStatusSelectedNames = eventStatusSelected.map((event) => event.label)

    const start = dateFilter.start.toISOString()
    const end = dateFilter.end.toISOString()
    onChangeFilter(start, end, clientIds, cameraIds, eventStatusSelectedNames)
  }, [camerasSelected, clientsSelected, dateFilter, eventStatusSelected])

  useEffect(() => {
    setClientsSelected([])
    setCamerasSelected([])
    setCameraOptions(clients?.data.data.entities.flatMap((client) => client.cameras) ?? [])
  }, [clients])

  return (
    <Box
      padding='30px 20px'
      display='flex'
      align='center'
      justifyContent='space-between'
      backgroundColor='#ffff'
      borderRadius='16px'
      margin='20px'
      columnGap='10px'>
      <Box display='flex' direction='row' style={{alignItems: 'center', gap: '15px'}}>
        <Box height={'40px'} borderRadius={'8px'} style={{padding: '0px 4px'}} display='flex' align='center' justifyContent='center' border='1px solid #D0D5DD'>
          {dashboardsOptions.map((option) => {
            return (
              <Box
                key={option.id}
                display='flex'
                direction='column'
                align='center'
                justifyContent='center'
                borderRadius={'4px'}
                padding={'6px 16px'}
                width='104px'
                onClick={() => setDashboardSelected(option.id)}
                style={{backgroundColor: dashboardSelected === option.id ? styledTheme.colors.primary : styledTheme.colors.white, cursor: 'pointer'}}>
                <Typography
                  variant='label'
                  color={dashboardSelected === option.id ? styledTheme.colors.white : '#98A2B3'}
                  weight={500}
                  size='14px'
                  cursor='pointer'
                  onClick={() => setDashboardSelected(option.id)}>
                  {option.label}
                </Typography>
              </Box>
            )
          })}
        </Box>

        {dashboardSelected !== 'analytic' && (
          <Tooltip
            placement='right'
            title={`Última atualização: ${formatDateTime(refreshedAt.toISOString()).formattedDate} às ${formatDateTime(refreshedAt.toISOString()).formattedTime}`}>
            <Box
              onClick={() => handleRefresh()}
              style={{cursor: 'pointer', transition: 'transform 0.3s ease-in-out', transform: isRotating ? 'rotate(360deg)' : 'none'}}>
              <RefreshIcon />
            </Box>
          </Tooltip>
        )}
      </Box>

      <Box
        display={dashboardSelected === 'events' ? 'grid' : 'flex'}
        style={{
          gap: '10px',
          gridTemplateColumns: 'repeat(3,1fr)',
        }}>
        <Dropdown
          width='240px'
          height='40px'
          borderColor='#9aa4b2'
          fontSize='12px'
          placeholder='Empresa'
          selected={companySeleted}
          onChangeSelected={(e) => {
            setCompanySelected(e)
            setSelectedCompanyId(Number(e.id))
          }}
          showFloatingLabel
          items={companies.map((it) => {
            return {
              id: String(it.id),
              label: it.name,
            }
          })}
        />

        <MultiSelect onChange={(selections) => setClientsSelected(selections)} value={clientsSelected} loading={false}>
          <MultiSelectValue placeholder={'Selecione um cliente'} width='240px' borderColor='#9aa4b2' style={{fontSize: '12px'}} showFloatingLabel />
          <MultiSelectContent>
            <MultiSelectScrollView>
              {clients?.data.data.entities.map((item) => (
                <MultiSelectItem value={item.id.toString()} label={item.name}>
                  {item.name}
                </MultiSelectItem>
              ))}
            </MultiSelectScrollView>
          </MultiSelectContent>
        </MultiSelect>

        <MultiSelect onChange={(selections) => setCamerasSelected(selections)} value={camerasSelected} loading={false}>
          <MultiSelectValue placeholder={'Selecione uma câmera'} width='240px' borderColor='#9aa4b2' style={{fontSize: '12px'}} showFloatingLabel />
          <MultiSelectContent>
            <MultiSelectScrollView>
              {cameraOptions.map((item) => (
                <MultiSelectItem value={item.id.toString()} label={item.name}>
                  {item.name}
                </MultiSelectItem>
              ))}
            </MultiSelectScrollView>
          </MultiSelectContent>
        </MultiSelect>

        {dashboardSelected === 'events' && (
          <MultiSelect onChange={(selections) => setEventStatusSelected(selections)} value={eventStatusSelected} loading={false}>
            <MultiSelectValue placeholder={'Selecione o tipo de evento'} borderColor='#9aa4b2' style={{fontSize: '12px'}} showFloatingLabel />
            <MultiSelectContent>
              <MultiSelectScrollView>
                {EventStatusNameValues.map((status, index) => (
                  <MultiSelectItem value={index.toString()} label={status}>
                    {status}
                  </MultiSelectItem>
                ))}
              </MultiSelectScrollView>
            </MultiSelectContent>
          </MultiSelect>
        )}
        <CustomCalendar initialDates={[dateFilter.start, dateFilter.end]} setDate={(dates) => setDateFilter({start: dates[0], end: dates[1]})} hideRuler />
        <ExportDataButton
          dashboardSelected={dashboardSelected}
          start={filter.start}
          end={filter.end}
          filter={{cameraIds: filter.cameraIds, clientIds: filter.clientIds, status: filter.eventStatuses}}
        />
      </Box>
    </Box>
  )
}

export default DashboardHeader
